/**
 * Permission identifiers are defined by the backend (`app/permissions/policy.py`) and arrive
 * with every board / workspace payload. The UI only *reflects* them (hiding or disabling a
 * control); the server enforces every action.
 */
export type PermissionId = string;

export const PERMISSIONS = {
  boardView: 'board.view',
  boardEditDetails: 'board.edit_details',
  boardManageColumns: 'board.manage_columns',
  boardManageTaskLabels: 'board.manage_task_labels',
  boardManageMembers: 'board.manage_members',
  boardManagePermissions: 'board.manage_permissions',
  boardViewActivity: 'board.view_activity',
  boardExport: 'board.export',
  boardImport: 'board.import',
  boardRestore: 'board.restore',
  boardDelete: 'board.delete',
  boardTransfer: 'board.transfer',
  taskCreate: 'task.create',
  taskEditUnassigned: 'task.edit_unassigned',
  taskEditShared: 'task.edit_shared',
  taskEditAssignedToMe: 'task.edit_assigned_to_me',
  taskEditAssignedToOthers: 'task.edit_assigned_to_others',
  taskDelete: 'task.delete',
  taskMove: 'task.move',
  taskAssign: 'task.assign',
  taskManageLabels: 'task.manage_labels',
  taskEditSchedule: 'task.edit_schedule',
  taskViewHistory: 'task.view_history',
  taskRestoreVersions: 'task.restore_versions',
  taskViewAttachments: 'task.view_attachments',
  taskEditAttachments: 'task.edit_attachments',
  taskComment: 'task.comment',
  taskModerateComments: 'task.moderate_comments',
  taskWatch: 'task.watch',
  workspaceView: 'workspace.view',
  workspaceUpdateSettings: 'workspace.update_settings',
  workspaceCreateBoard: 'workspace.create_board',
  workspaceManageMembers: 'workspace.manage_members',
  workspaceAssignRoles: 'workspace.assign_roles',
  workspaceManageCustomRoles: 'workspace.manage_custom_roles',
  workspaceManageTeams: 'workspace.manage_teams',
  workspaceManageBoardLabels: 'workspace.manage_board_labels',
  workspaceConfigureBoardAccess: 'workspace.configure_board_access',
  workspaceManageInvitations: 'workspace.manage_invitations',
  workspaceReviewRequests: 'workspace.review_requests',
  workspaceReviewTransfers: 'workspace.review_transfers',
  workspaceViewAudit: 'workspace.view_audit',
  workspaceManageBoardTrash: 'workspace.manage_board_trash',
  workspaceManageTeamLeaders: 'workspace.manage_team_leaders',
  workspaceManageBoardOwners: 'workspace.manage_board_owners',
  workspaceManageNotificationPolicy: 'workspace.manage_notification_policy',
  workspaceCreatePalettes: 'workspace.create_palettes',
  workspaceManageOwnPalettes: 'workspace.manage_own_palettes',
  workspaceManageAllPalettes: 'workspace.manage_all_palettes',
  workspaceDelete: 'workspace.delete',
  workspaceTransferOwnership: 'workspace.transfer_ownership',
} as const satisfies Record<string, PermissionId>;

export function can(
  permissions: readonly PermissionId[] | undefined | null,
  permission: PermissionId,
): boolean {
  return permissions?.includes(permission) ?? false;
}

/** Whether any of the task-editing permissions is held (the card form is then editable). */
export function canEditSomeTask(permissions: readonly PermissionId[] | undefined | null): boolean {
  return (
    can(permissions, PERMISSIONS.taskEditUnassigned) ||
    can(permissions, PERMISSIONS.taskEditShared) ||
    can(permissions, PERMISSIONS.taskEditAssignedToMe) ||
    can(permissions, PERMISSIONS.taskEditAssignedToOthers)
  );
}

/** Board-level permissions (the individual-exception editor lists these). */
export const BOARD_SCOPE_PERMISSIONS: readonly PermissionId[] = [
  'board.view',
  'board.edit_details',
  'board.manage_columns',
  'board.manage_task_labels',
  'board.manage_members',
  'board.manage_permissions',
  'board.view_activity',
  'board.export',
  'board.import',
  'board.restore',
  'board.delete',
  'board.transfer',
  'task.create',
  'task.view_assigned_to_me',
  'task.view_assigned_to_others',
  'task.view_shared',
  'task.view_unassigned',
  'task.edit_assigned_to_me',
  'task.edit_assigned_to_others',
  'task.edit_shared',
  'task.edit_unassigned',
  'task.delete',
  'task.move',
  'task.assign',
  'task.manage_labels',
  'task.edit_schedule',
  'task.view_history',
  'task.restore_versions',
  'task.view_attachments',
  'task.edit_attachments',
  'task.comment',
  'task.moderate_comments',
  'task.watch',
];

export const isBoardScopePermission = (permission: PermissionId): boolean =>
  !permission.startsWith('workspace.');

/** Board-scope permissions grouped for the matrix editor (mirrors the backend catalog). */
export const BOARD_PERMISSION_GROUPS: Record<string, string[]> = {
  board: [
    'board.view',
    'board.edit_details',
    'board.manage_columns',
    'board.manage_task_labels',
    'board.manage_members',
    'board.manage_permissions',
    'board.view_activity',
    'board.export',
    'board.import',
    'board.restore',
    'board.delete',
    'board.transfer',
  ],
  task_visibility: [
    'task.view_assigned_to_me',
    'task.view_assigned_to_others',
    'task.view_shared',
    'task.view_unassigned',
  ],
  task_editing: [
    'task.create',
    'task.edit_assigned_to_me',
    'task.edit_assigned_to_others',
    'task.edit_shared',
    'task.edit_unassigned',
    'task.delete',
    'task.move',
    'task.assign',
    'task.manage_labels',
    'task.edit_schedule',
  ],
  task_content: [
    'task.view_history',
    'task.restore_versions',
    'task.view_attachments',
    'task.edit_attachments',
    'task.comment',
    'task.moderate_comments',
    'task.watch',
  ],
};

/**
 * The ONE nondelegable permission: only the workspace owner moves the workspace to someone else.
 * Everything else (deleting the workspace, transferring boards ...) can be delegated.
 */
export const OWNER_ONLY_PERMISSIONS: readonly PermissionId[] = ['workspace.transfer_ownership'];
